import React, { useState, useEffect, useMemo } from 'react';
import { X, Plus, Trash2, BookOpen, Save, GripVertical, Clock, Search, Wand2, ArrowUpDown, Lightbulb, Loader2, Check } from 'lucide-react';
import { LinkAvatar } from '@/components/icons/LinkAvatar';
import {
    DndContext,
    DragEndEvent,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { onboardingApi, type ProgramAssistAction, type ProgramAssistResponse } from '@/lib/api/onboardingApi';
import { moduleApi } from '@/lib/api/moduleApi';
import { Module } from '@/lib/models/module';
import { OnboardingProgram } from '@/lib/models/onboarding';
import { ActionButton } from '@/components/custom-ui/action-button';
import { IconChip } from '@/components/custom-ui/IconChip';
import { IslandSlideOver } from '@/components/custom-ui/IslandSlideOver';
import { parseMinutes, formatMinutes } from '@/lib/duration';

// One draggable course card in the program's ordered course list. Module IDs are
// stable + unique within the list, so they double as dnd-kit sortable ids.
const SortableModuleRow: React.FC<{
    id: string;
    index: number;
    name: string;
    duration?: string;
    onRemove: () => void;
}> = ({ id, index, name, duration, onRemove }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 10 : undefined,
    };
    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`flex items-center gap-2.5 p-2.5 bg-white border-2 rounded-xl ${isDragging ? 'border-[#1A4D4F]/30 shadow-md' : 'border-gray-100'}`}
        >
            <button
                type="button"
                {...attributes}
                {...listeners}
                aria-label="Dra för att ändra ordning"
                className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 touch-none flex-shrink-0"
            >
                <GripVertical className="w-4 h-4" />
            </button>
            <span className="w-6 h-6 bg-[#A4C3B2]/40 text-[#1A4D4F] rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">{index + 1}</span>
            <IconChip icon={BookOpen} size="sm" />
            <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 truncate">{name}</p>
                {duration && (
                    <p className="text-xs text-gray-400 flex items-center gap-1"><Clock className="w-3 h-3" /> {duration}</p>
                )}
            </div>
            <button
                type="button"
                onClick={onRemove}
                title="Ta bort modul"
                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg flex-shrink-0 transition-colors"
            >
                <Trash2 className="w-4 h-4" />
            </button>
        </div>
    );
};

interface ProgramFormModalProps {
    program?: OnboardingProgram; // present = edit mode (C1)
    onClose: () => void;
    onSuccess: () => void;
}

const ProgramFormModal: React.FC<ProgramFormModalProps> = ({ program, onClose, onSuccess }) => {
    const isEdit = !!program;
    const [name, setName] = useState(program?.name ?? '');
    const [description, setDescription] = useState(program?.description ?? '');
    const [isDefault, setIsDefault] = useState(program?.is_default ?? false);
    const [selectedModuleIds, setSelectedModuleIds] = useState<string[]>(program?.module_ids ?? []);
    const [availableModules, setAvailableModules] = useState<Module[]>([]);
    // Courses referenced by the program but NOT in the own+global pool (e.g. legacy
    // or otherwise out-of-scope). Fetched individually so their rows show a real
    // title instead of a raw UUID. `null` = looked up but unresolvable.
    const [extraModules, setExtraModules] = useState<Record<string, Module | null>>({});
    const [addQuery, setAddQuery] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // "Link" AI assist — optional, preview-before-apply.
    const [assisting, setAssisting] = useState<ProgramAssistAction | null>(null);
    const [assistError, setAssistError] = useState<string | null>(null);
    const [preview, setPreview] = useState<{ action: ProgramAssistAction; res: ProgramAssistResponse } | null>(null);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
    );

    const moduleById = useMemo(() => {
        const m: Record<string, Module> = {};
        availableModules.forEach((mod) => { m[mod.id] = mod; });
        return m;
    }, [availableModules]);

    // Resolve a course from either pool (own+global first, then the fetched extras).
    const resolveModule = (id: string): Module | undefined => moduleById[id] ?? extraModules[id] ?? undefined;
    // Display name for a selected course — never a raw UUID.
    const courseName = (id: string): string => resolveModule(id)?.module_name || 'Modul (ej tillgänglig)';

    const totalMinutes = useMemo(
        () => selectedModuleIds.reduce((sum, id) => sum + parseMinutes(resolveModule(id)?.estimated_duration), 0),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedModuleIds, moduleById, extraModules],
    );

    const addable = useMemo(() => {
        const q = addQuery.trim().toLowerCase();
        return availableModules
            // #75 — only PUBLISHED courses can be newly added to a program. Drafts
            // would reach candidates before the employer published them; archived
            // courses are retired (they may still linger in this program if they
            // were added while live, but can't be newly picked). The backend
            // enforces the same rule for newly-added courses.
            .filter((c) => (c.status ?? 'draft') === 'published')
            .filter((c) => !selectedModuleIds.includes(c.id))
            .filter((c) => !q || c.module_name.toLowerCase().includes(q));
    }, [availableModules, selectedModuleIds, addQuery]);

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        setSelectedModuleIds((prev) => {
            const from = prev.indexOf(String(active.id));
            const to = prev.indexOf(String(over.id));
            if (from === -1 || to === -1) return prev;
            return arrayMove(prev, from, to);
        });
    };

    useEffect(() => {
        moduleApi.getMyModules().then(modules => setAvailableModules(modules || [])).catch(() => {});
    }, []);

    // Back-fill titles for any selected course that isn't in the own+global pool by
    // fetching it directly — so program rows never render a raw UUID. Unresolvable
    // ids are cached as `null` to avoid re-fetching every render.
    useEffect(() => {
        const missing = selectedModuleIds.filter((id) => !moduleById[id] && !(id in extraModules));
        if (missing.length === 0) return;
        let cancelled = false;
        Promise.all(
            missing.map(async (id) => [id, await moduleApi.getModuleById(id).catch(() => null)] as const),
        ).then((pairs) => {
            if (cancelled) return;
            setExtraModules((prev) => {
                const next = { ...prev };
                pairs.forEach(([id, m]) => { next[id] = m; });
                return next;
            });
        });
        return () => { cancelled = true; };
    }, [selectedModuleIds, moduleById, extraModules]);

    const runAssist = async (action: ProgramAssistAction) => {
        if (!name.trim()) {
            setAssistError('Ge programmet ett namn först så Link har något att utgå från.');
            return;
        }
        setAssisting(action);
        setAssistError(null);
        setPreview(null);
        try {
            const res = await onboardingApi.assistProgram({
                action,
                name: name.trim(),
                description: description.trim() || undefined,
                courses: selectedModuleIds.map((id) => {
                    const m = resolveModule(id);
                    return { id, name: m?.module_name || 'Okänd modul', description: m?.module_description, estimated_duration: m?.estimated_duration };
                }),
                available: action === 'suggest_courses'
                    ? availableModules
                        .filter((m) => (m.status ?? 'draft') === 'published') // #75 — only suggest published courses
                        .filter((m) => !selectedModuleIds.includes(m.id))
                        .map((m) => ({ id: m.id, name: m.module_name, description: m.module_description, estimated_duration: m.estimated_duration }))
                    : undefined,
            });
            setPreview({ action, res });
        } catch (e: any) {
            setAssistError(e?.message || 'Link kunde inte hjälpa till just nu. Försök igen.');
        } finally {
            setAssisting(null);
        }
    };

    const applyImprovedDescription = () => {
        if (preview?.res.improved_description != null) setDescription(preview.res.improved_description);
        setPreview(null);
    };

    const applyReorder = () => {
        const ids = (preview?.res.ordered_course_ids ?? []).filter((id) => selectedModuleIds.includes(id));
        // Append any current ids the model omitted, so nothing is silently dropped.
        const missing = selectedModuleIds.filter((id) => !ids.includes(id));
        if (ids.length > 0) setSelectedModuleIds([...ids, ...missing]);
        setPreview(null);
    };

    const applyAddOne = (id: string) => {
        setSelectedModuleIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
        setPreview((p) => (p ? { ...p, res: { ...p.res, add_course_ids: (p.res.add_course_ids ?? []).filter((x) => x !== id) } } : p));
    };

    const applyRemoveOne = (id: string) => {
        setSelectedModuleIds((prev) => prev.filter((x) => x !== id));
        setPreview((p) => (p ? { ...p, res: { ...p.res, remove_course_ids: (p.res.remove_course_ids ?? []).filter((x) => x !== id) } } : p));
    };

    const handleSubmit = async () => {
        if (!name.trim()) {
            setError('Programnamn krävs');
            return;
        }
        setIsSubmitting(true);
        setError(null);
        const payload = {
            name: name.trim(),
            description: description.trim() || undefined,
            module_ids: selectedModuleIds,
            is_default: isDefault,
        };
        try {
            if (isEdit && program) {
                await onboardingApi.updateProgram(program.id, payload);
            } else {
                await onboardingApi.createProgram(payload);
            }
            onSuccess();
        } catch (err: any) {
            setError(err?.message || 'Något gick fel.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <IslandSlideOver onClose={onClose} widthClass="max-w-lg">
                <div className="flex items-center justify-between p-5 border-b border-gray-100 flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <IconChip icon={BookOpen} tone="brand" />
                        <h2 className="text-lg font-bold text-gray-900">{isEdit ? 'Redigera program' : 'Nytt program'}</h2>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                <div className="p-5 space-y-5 overflow-y-auto flex-1">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Programnamn</label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="t.ex. Introduktionsprogram för nya medarbetare"
                            className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1A4D4F]"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Beskrivning (valfritt)</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Beskriv vad programmet innehåller..."
                            className="w-full h-20 p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#1A4D4F]"
                        />
                    </div>

                    {/* Course list — ordered, draggable cards */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="text-sm font-medium text-gray-700">Moduler i programmet</label>
                            <span className="text-xs text-gray-400">
                                {selectedModuleIds.length} {selectedModuleIds.length === 1 ? 'modul' : 'moduler'}
                                {totalMinutes > 0 && ` · ~${formatMinutes(totalMinutes)} totalt`}
                            </span>
                        </div>

                        {selectedModuleIds.length > 0 ? (
                            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                <SortableContext items={selectedModuleIds} strategy={verticalListSortingStrategy}>
                                    <div className="space-y-2 mb-3">
                                        {selectedModuleIds.map((moduleId, index) => (
                                            <SortableModuleRow
                                                key={moduleId}
                                                id={moduleId}
                                                index={index}
                                                name={courseName(moduleId)}
                                                duration={resolveModule(moduleId)?.estimated_duration}
                                                onRemove={() => setSelectedModuleIds(prev => prev.filter(id => id !== moduleId))}
                                            />
                                        ))}
                                    </div>
                                </SortableContext>
                            </DndContext>
                        ) : (
                            <div className="mb-3 p-4 text-center border-2 border-dashed border-gray-200 rounded-xl">
                                <p className="text-sm text-gray-400">Inga moduler ännu — lägg till nedan. Dra för att ändra ordning.</p>
                            </div>
                        )}

                        {/* Searchable course picker */}
                        <div className="border border-gray-200 rounded-xl overflow-hidden">
                            <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 bg-gray-50">
                                <Search className="w-4 h-4 text-gray-400" />
                                <input
                                    type="text"
                                    value={addQuery}
                                    onChange={(e) => setAddQuery(e.target.value)}
                                    placeholder="Lägg till modul — sök bland dina moduler…"
                                    className="flex-1 bg-transparent text-sm outline-none"
                                />
                            </div>
                            <div className="max-h-48 overflow-y-auto">
                                {addable.length === 0 ? (
                                    <p className="px-3 py-3 text-xs text-gray-400">
                                        {availableModules.length === 0 ? 'Inga moduler ännu — skapa moduler under Moduler.' : 'Inga fler moduler att lägga till.'}
                                    </p>
                                ) : (
                                    addable.map((c) => (
                                        <button
                                            key={c.id}
                                            type="button"
                                            onClick={() => { setSelectedModuleIds(prev => [...prev, c.id]); setAddQuery(''); }}
                                            className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-gray-50 transition-colors"
                                        >
                                            <Plus className="w-4 h-4 text-[#1A4D4F] flex-shrink-0" />
                                            <span className="text-sm text-gray-800 truncate flex-1">{c.module_name}</span>
                                            {c.estimated_duration && (
                                                <span className="text-xs text-gray-400 flex items-center gap-1 flex-shrink-0">
                                                    <Clock className="w-3 h-3" /> {c.estimated_duration}
                                                </span>
                                            )}
                                        </button>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ✨ Link assist — optional, preview before applying */}
                    <div className="rounded-xl border border-[#A4C3B2]/70 bg-[#A4C3B2]/10 p-3.5 space-y-3">
                        <div className="flex items-center gap-2">
                            <LinkAvatar className="w-5 h-5" />
                            <p className="text-sm font-semibold text-[#1A4D4F]">Link kan hjälpa till</p>
                            <span className="text-xs text-gray-500">— valfritt</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {([
                                { action: 'improve_description' as const, icon: Wand2, label: 'Förbättra beskrivningen', disabled: false },
                                { action: 'reorder' as const, icon: ArrowUpDown, label: 'Föreslå ordning', disabled: selectedModuleIds.length < 2 },
                                { action: 'suggest_courses' as const, icon: Lightbulb, label: 'Föreslå moduler', disabled: false },
                            ]).map(({ action, icon: Icon, label, disabled }) => (
                                <button
                                    key={action}
                                    type="button"
                                    onClick={() => runAssist(action)}
                                    disabled={!!assisting || disabled}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-white border border-[#1A4D4F]/20 text-[#1A4D4F] hover:bg-[#1A4D4F] hover:text-white disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-[#1A4D4F] disabled:cursor-not-allowed transition-colors"
                                >
                                    {assisting === action ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
                                    {label}
                                </button>
                            ))}
                        </div>

                        {assistError && <p className="text-xs text-red-600">{assistError}</p>}

                        {/* Preview — improve description */}
                        {preview?.action === 'improve_description' && (
                            <div className="rounded-lg bg-white border border-gray-200 p-3 space-y-2">
                                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Förslag på beskrivning</p>
                                <p className="text-sm text-gray-800 whitespace-pre-wrap">{preview.res.improved_description || '—'}</p>
                                {preview.res.rationale && <p className="text-xs text-gray-400 italic">{preview.res.rationale}</p>}
                                <div className="flex gap-2 pt-1">
                                    <button type="button" onClick={applyImprovedDescription} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-[#1A4D4F] text-white hover:bg-[#143a3c] transition-colors">
                                        <Check className="w-3.5 h-3.5" /> Använd
                                    </button>
                                    <button type="button" onClick={() => setPreview(null)} className="px-3 py-1.5 text-xs font-medium rounded-lg text-gray-600 hover:bg-gray-100 transition-colors">Avfärda</button>
                                </div>
                            </div>
                        )}

                        {/* Preview — reorder */}
                        {preview?.action === 'reorder' && (
                            <div className="rounded-lg bg-white border border-gray-200 p-3 space-y-2">
                                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Föreslagen ordning</p>
                                <ol className="space-y-1">
                                    {(preview.res.ordered_course_ids ?? []).filter((id) => moduleById[id]).map((id, i) => (
                                        <li key={id} className="flex items-center gap-2 text-sm text-gray-800">
                                            <span className="w-5 h-5 bg-[#A4C3B2]/40 text-[#1A4D4F] rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0">{i + 1}</span>
                                            <span className="truncate">{moduleById[id]?.module_name}</span>
                                        </li>
                                    ))}
                                </ol>
                                {preview.res.rationale && <p className="text-xs text-gray-400 italic">{preview.res.rationale}</p>}
                                <div className="flex gap-2 pt-1">
                                    <button type="button" onClick={applyReorder} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-[#1A4D4F] text-white hover:bg-[#143a3c] transition-colors">
                                        <Check className="w-3.5 h-3.5" /> Använd ordning
                                    </button>
                                    <button type="button" onClick={() => setPreview(null)} className="px-3 py-1.5 text-xs font-medium rounded-lg text-gray-600 hover:bg-gray-100 transition-colors">Avfärda</button>
                                </div>
                            </div>
                        )}

                        {/* Preview — suggest courses */}
                        {preview?.action === 'suggest_courses' && (() => {
                            const adds = (preview.res.add_course_ids ?? []).filter((id) => moduleById[id] && !selectedModuleIds.includes(id));
                            const removes = (preview.res.remove_course_ids ?? []).filter((id) => selectedModuleIds.includes(id));
                            if (adds.length === 0 && removes.length === 0) {
                                return (
                                    <div className="rounded-lg bg-white border border-gray-200 p-3 space-y-2">
                                        <p className="text-sm text-gray-600">Link har inga ändringar att föreslå — modullistan ser bra ut.</p>
                                        {preview.res.rationale && <p className="text-xs text-gray-400 italic">{preview.res.rationale}</p>}
                                        <button type="button" onClick={() => setPreview(null)} className="px-3 py-1.5 text-xs font-medium rounded-lg text-gray-600 hover:bg-gray-100 transition-colors">Stäng</button>
                                    </div>
                                );
                            }
                            return (
                                <div className="rounded-lg bg-white border border-gray-200 p-3 space-y-3">
                                    {adds.length > 0 && (
                                        <div className="space-y-1.5">
                                            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Föreslås läggas till</p>
                                            {adds.map((id) => (
                                                <div key={id} className="flex items-center gap-2">
                                                    <span className="text-sm text-gray-800 truncate flex-1">{moduleById[id]?.module_name}</span>
                                                    <button type="button" onClick={() => applyAddOne(id)} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg text-[#1A4D4F] hover:bg-[#1A4D4F] hover:text-white border border-[#1A4D4F]/20 transition-colors flex-shrink-0">
                                                        <Plus className="w-3 h-3" /> Lägg till
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {removes.length > 0 && (
                                        <div className="space-y-1.5">
                                            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Föreslås tas bort</p>
                                            {removes.map((id) => (
                                                <div key={id} className="flex items-center gap-2">
                                                    <span className="text-sm text-gray-800 truncate flex-1">{moduleById[id]?.module_name}</span>
                                                    <button type="button" onClick={() => applyRemoveOne(id)} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg text-red-600 hover:bg-red-50 border border-red-200 transition-colors flex-shrink-0">
                                                        <Trash2 className="w-3 h-3" /> Ta bort
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {preview.res.rationale && <p className="text-xs text-gray-400 italic">{preview.res.rationale}</p>}
                                    <button type="button" onClick={() => setPreview(null)} className="px-3 py-1.5 text-xs font-medium rounded-lg text-gray-600 hover:bg-gray-100 transition-colors">Stäng</button>
                                </div>
                            );
                        })()}
                    </div>

                    {/* Default toggle */}
                    <div className="flex items-start justify-between gap-4 p-3 bg-gray-50 rounded-xl">
                        <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900">Standardprogram</p>
                            <p className="text-xs text-gray-500">Tilldelas automatiskt till nya medarbetare.</p>
                        </div>
                        <button
                            type="button"
                            role="switch"
                            aria-checked={isDefault}
                            onClick={() => setIsDefault((v) => !v)}
                            className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${isDefault ? 'bg-[#1A4D4F]' : 'bg-gray-300'}`}
                        >
                            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${isDefault ? 'translate-x-5' : ''}`} />
                        </button>
                    </div>

                    {error && <p className="text-sm text-red-600">{error}</p>}
                </div>

                <div className="flex justify-end gap-3 p-5 border-t border-gray-100 flex-shrink-0">
                    <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl">
                        Avbryt
                    </button>
                    <ActionButton icon={isEdit ? Save : Plus} onClick={handleSubmit} disabled={isSubmitting}>
                        {isSubmitting ? (isEdit ? 'Sparar...' : 'Skapar...') : (isEdit ? 'Spara' : 'Skapa Program')}
                    </ActionButton>
                </div>
        </IslandSlideOver>
    );
};

export default ProgramFormModal;
